// src/components/Dashboard/CoachDashboard/ProgramSelectorBar.jsx
// Dropdown program selector for coach dashboard - matches Club Owner Dashboard style

import React, { useState } from 'react';
import { 
  FaChevronDown, FaCheck, FaChalkboardTeacher, 
  FaExternalLinkAlt, FaBuilding 
} from 'react-icons/fa';
import styles from './ProgramSelectorBar.module.css';

const SPORT_ICONS = {
  'Soccer': '⚽',
  'Basketball': '🏀',
  'Football': '🏈',
  'Volleyball': '🏐',
  'Baseball': '⚾',
  'Tennis': '🎾',
  'Hockey': '🏒',
  'Rugby': '🏉',
  'Lacrosse': '🥍',
  'Swimming': '🏊',
  'Track': '🏃',
  'Golf': '⛳',
  'default': '🏆'
};

const getSportIcon = (sport) => {
  return SPORT_ICONS[sport] || SPORT_ICONS.default;
};

const getRoleLabel = (role) => {
  switch (role) {
    case 'HeadCoach':
      return 'Head Coach';
    case 'AssistantCoach':
      return 'Assistant Coach';
    case 'Coach':
      return 'Coach';
    default:
      return role || 'Coach';
  }
};

const getRoleBadge = (role) => {
  return role === 'HeadCoach' ? 'HC' : 'AC';
};

const ProgramSelectorBar = ({ programs, selectedProgramId, onSelectProgram }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  if (!programs || programs.length === 0) {
    return null;
  }

  const selectedProgram = programs.find(p => p._id === selectedProgramId) || programs[0];
  
  // Extract club info from various possible data structures
  const clubData = selectedProgram?.clubId || selectedProgram?.club || {};
  const clubId = clubData?._id || clubData;
  const clubName = clubData?.name || selectedProgram?.clubName || 'View Club';

  const handleProgramSelect = (programId) => {
    onSelectProgram(programId);
    setShowDropdown(false);
  };

  return (
    <div className={styles.container}>
      {/* Program Selector Dropdown */}
      <div className={styles.selectorWrapper}>
        <div className={styles.programSelector}>
          <button 
            className={styles.selectorButton}
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <div className={styles.programInfo}>
              <span className={styles.programIcon}>
                {selectedProgram?.image ? (
                  <img src={selectedProgram.image} alt="" />
                ) : (
                  getSportIcon(selectedProgram?.sport)
                )}
              </span>
              <div className={styles.programDetails}>
                <div className={styles.programNameRow}>
                  <span className={styles.programName}>
                    {selectedProgram?.name || 'Select Program'}
                  </span>
                  <span className={styles.roleBadge}>
                    {getRoleBadge(selectedProgram?.userRole)}
                  </span>
                </div>
                <span className={styles.programRole}>
                  <FaChalkboardTeacher className={styles.roleIcon} />
                  {getRoleLabel(selectedProgram?.userRole)}
                </span>
              </div>
            </div>
            <FaChevronDown className={`${styles.dropdownIcon} ${showDropdown ? styles.open : ''}`} />
          </button>
          
          {showDropdown && (
            <div className={styles.dropdown}>
              {programs.map((program) => {
                const isSelected = program._id === selectedProgramId;
                
                return (
                  <button
                    key={program._id}
                    className={`${styles.dropdownOption} ${isSelected ? styles.selected : ''}`}
                    onClick={() => handleProgramSelect(program._id)}
                  >
                    <span className={styles.optionIcon}>
                      {program.image ? <img src={program.image} alt="" /> : getSportIcon(program.sport)}
                    </span>
                    <div className={styles.optionDetails}>
                      <span className={styles.optionName}>{program.name}</span>
                      <span className={styles.optionRole}>{getRoleLabel(program.userRole)}</span>
                    </div>
                    <span className={styles.optionBadge}>{getRoleBadge(program.userRole)}</span>
                    {isSelected && <FaCheck className={styles.checkIcon} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* View Program Link */}
        {selectedProgramId && (
          <a
            href={`${window.location.origin}/program/${selectedProgramId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.viewLink}
            title="View Program Page"
          >
            <FaExternalLinkAlt />
          </a>
        )}
      </div>

      {/* Right side: Program Count & Club Link */}
      <div className={styles.rightSection}>
        {programs.length > 1 && (
          <div className={styles.programCount}>
            {programs.length} Programs
          </div>
        )}
        
        {/* Club Link */}
        {clubId && (
          <a
            href={`${window.location.origin}/club/${clubId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.clubLink}
            title={`View ${clubName}`}
          >
            <FaBuilding className={styles.clubLinkIcon} />
            <span className={styles.clubLinkName}>{clubName}</span>
            <FaExternalLinkAlt className={styles.externalIcon} />
          </a>
        )}
      </div>
    </div>
  );
};

export default ProgramSelectorBar;
